import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Save, ArrowBack, CloudUpload, Delete } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { createFoodCategory, updateFoodCategory } from "../../services/Api";

const CreateAndModifyFoodCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderError, setOrderError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    displayOrder: "1",
    isActive: true,
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (isEdit && location.state?.category) {
      const data = location.state.category;
      setFormData({
        name: data.name || "",
        displayOrder: data.displayOrder?.toString() || "1",
        isActive: data.isActive !== undefined ? data.isActive : true,
      });
      if (data.image) {
        setPreview(data.image);
      }
    } else if (isEdit && !location.state?.category) {
      setError("Category data not found. Please go back and try again.");
    }
  }, [id, isEdit, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === "displayOrder") {
      if (finalValue === "") {
        finalValue = "";
      } else if (String(value).match(/\.\d+/)) {
        finalValue = String(Math.round(parseFloat(value)));
      } else {
        finalValue = String(value).replace(/[^0-9.]/g, "");
      }

      if (
        finalValue !== "" &&
        (Number(finalValue) < 1 || Number(finalValue) > 100)
      ) {
        setOrderError("Display Order must be between 1 and 100");
      } else {
        setOrderError("");
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    const categoryName = formData.name ? formData.name.trim() : "";
    if (!categoryName) {
      setError("Please enter a category name.");
      setLoading(false);
      return;
    }

    const orderVal = formData.displayOrder;
    if (
      !/^\d+$/.test(String(orderVal).trim()) ||
      Number(orderVal) > 100 ||
      Number(orderVal) < 1
    ) {
      setError("Display Order must be a valid integer between 1 and 100.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("displayOrder", formData.displayOrder);
    data.append("isActive", formData.isActive);
    if (image) {
      if (!image.type.startsWith("image/")) {
        setError("Only images are allowed");
        setLoading(false);
        return;
      }
      data.append("menuImages", image);
    }

    try {
      const response = isEdit
        ? await updateFoodCategory(id, data)
        : await createFoodCategory(data);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate("/food-categories"), 1500);
      } else {
        setError(response.message || "Failed to save food category");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred while saving");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/food-categories");
            }}
          >
            Food Categories
          </Link>
          <Typography color="text.primary">
            {isEdit ? "Edit" : "Create"} Category
          </Typography>
        </Breadcrumbs>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate("/food-categories")}
            sx={{ border: "1px solid", borderColor: "divider" }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isEdit ? "Edit Food Category" : "Create Food Category"}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        {success && (
          <Alert severity="success">
            Category saved successfully! Redirecting...
          </Alert>
        )}
        <br/>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Category Image
              </Typography>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  height: 250,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  position: "relative",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
              >
                {preview ? (
                  <>
                    <Box
                      component="img"
                      src={preview}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        "&:hover": { backgroundColor: "white" },
                      }}
                      onClick={() => {
                        setImage(null);
                        setPreview(null);
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload
                      sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Upload Category Image
                    </Typography>
                  </>
                )}
                <input
                  id="file-input-food-category"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleImageChange(e);
                    e.target.value = null;
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "center" }}
              >
                Note: Image size must be 300px * 300px
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  sx={{ mt: 2, fontSize: "10px !important" }}
                  startIcon={<CloudUpload />}
                  onClick={() =>
                    document.getElementById("file-input-food-category").click()
                  }
                >
                  Upload Image
                </Button>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Category Details
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Category Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Biryani, Pizza"
                  required
                />
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Display Order"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    placeholder="Higher numbers appear first"
                    error={Boolean(orderError)}
                    helperText={orderError}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "flex",
                      mt: 1,
                      textAlign: "start",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                    }}
                  >
                    Display Order must be between 1 and 100
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleSwitchChange}
                      color="primary"
                    />
                  }
                  label="Active Status"
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/food-categories")}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    fullWidth
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Save />
                      )
                    }
                    sx={{
                      py: 1.5,
                      backgroundColor: "primary.main",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    {isEdit ? "Update Category" : "Create Category"}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateAndModifyFoodCategory;
